import React, { createContext, useContext, useEffect, useState } from "react";
import {
  getClientsByUserName,
  createClient as createClientRequest,
  getClientById,
  updateClient as updateClientRequest,
} from "../../core/services/client.service";
import {
  createProject as createProjectRequest,
  deleteProject as deleteProjectRequest,
  getFileByProject,
  keyDatesToProjectStatus,
  updateIsArchived as updateProjectArchivedRequest,
  updateProject as updateProjectRequest,
} from "../../core/services/project.service";
import {
  createProjectType,
  getAllProjectTypes,
} from "../../core/services/projectType.service";
import { useAuth } from "./AuthContext";
import { useAlert } from "./AlertContext";
import { useAppState } from "./AppStateContext";
import { getUserErrorMessage } from "../../core/utils/apiErrorUtils";
import {
  countUploadFiles,
  createUploadProgressHandler,
  IDLE_SAVE_PROGRESS,
} from "../../core/utils/uploadProgress";

const ClientContext = createContext(null);
const PREFIX = "plambo_";
const getClientId = (client) => client?.clientId ?? client?.Id ?? client?.id ?? null;
const getProjectId = (project) =>
  project?.projectId ?? project?.Id ?? project?.id ?? null;

const countClientUploadFiles = (clientData) =>
  countUploadFiles(
    clientData?.knowledgeBank,
    clientData?.logo,
    clientData?.theme,
    clientData?.meeting,
    clientData?.otherDocuments,
  );

const isProjectArchived = (project) =>
  Boolean(
    project?.isArchived ??
      project?.IsArchived ??
      project?.is_archived ??
      project?.archived,
  );

const splitClientProjects = (clientObj) => {
  const list = Array.isArray(clientObj?.projects) ? clientObj.projects : [];
  const active = [];
  const archived = [];

  list.forEach((project) => {
    const normalized = {
      ...project,
      projectId: getProjectId(project),
    };
    if (isProjectArchived(project)) {
      archived.push(normalized);
    } else {
      active.push(normalized);
    }
  });

  return { active, archived };
};

function extractProjectFilesList(data) {
  if (data == null) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.files)) return data.files;
  if (Array.isArray(data.result)) return data.result;
  if (Array.isArray(data.items)) return data.items;
  return [];
}

export function useClient() {
  return useContext(ClientContext);
}

export function ClientProvider({ children }) {
  const { userInfo } = useAuth();
  const { showAlert } = useAlert();
  const { setActiveMode } = useAppState();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClientState] = useState(null);
  const [projects, setProjects] = useState([]);
  const [archivedProjects, setArchivedProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saveProgress, setSaveProgress] = useState(IDLE_SAVE_PROGRESS);

  const beginSaveProgress = (fileCount = 0) => {
    const total = Math.max(0, Number(fileCount) || 0);
    setSaveProgress({
      phase: total > 0 ? "uploading" : "saving",
      percent: total > 0 ? 0 : null,
      current: 0,
      total,
    });
  };

  const markSaveProcessing = () => {
    setSaveProgress((prev) => ({
      ...prev,
      phase: prev.total > 0 ? "processing" : "saving",
      percent: prev.total > 0 ? 100 : null,
      current: prev.total > 0 ? prev.total : prev.current,
    }));
  };

  const endSaveProgress = () => {
    setSaveProgress(IDLE_SAVE_PROGRESS);
  };

  const redirectToHomepage = () => {
    if (window.location.pathname !== "/") {
      window.location.href = "/";
    }
  };

  const refreshClientsForUser = async () => {
    const username = userInfo?.username;
    if (!username) return [];

    const data = await getClientsByUserName(username);
    setClients(data || []);
    return data || [];
  };

  const applyClientProjects = (clientObj) => {
    const { active, archived } = splitClientProjects(clientObj);
    setProjects(active);
    setArchivedProjects(archived);
    setSelectedProject(null);
  };

  const selectClient = async (clientObj, store = true) => {
    if (!clientObj) {
      setSelectedClientState(null);
      setProjects([]);
      setArchivedProjects([]);
      setSelectedProject(null);
      localStorage.removeItem(PREFIX + "selectedClient");
      setActiveMode(null);
      return;
    }

    setSelectedClientState(clientObj);
    setActiveMode(null);

    if (store) {
      localStorage.setItem(
        PREFIX + "selectedClient",
        JSON.stringify({
          clientId: getClientId(clientObj),
          timestamp: Date.now(),
        }),
      );
    }

    try {
      // Projects are included in getClientsByUserName — no per-client fetch needed.
      // const clientId = getClientId(clientObj);
      // const [proj, archivedProj] = await Promise.all([
      //   getProjectByClientId(clientId, false),
      //   getProjectByClientId(clientId, true),
      // ]);
      applyClientProjects(clientObj);
    } catch {
      setProjects([]);
      setArchivedProjects([]);
    }
  };

  const createClient = async (clientData) => {
    const trimmedName = String(clientData?.clientName ?? "").trim();
    if (!trimmedName) return;

    const fileCount = countClientUploadFiles(clientData);
    setLoading(true);
    beginSaveProgress(fileCount);

    try {
      const created = await createClientRequest(
        {
        ...clientData,
        clientName: trimmedName,
        domainName: String(clientData?.domainName ?? "").trim(),
        },
        {
          onUploadProgress: createUploadProgressHandler({
            fileCount,
            onProgress: setSaveProgress,
          }),
        },
      );

      markSaveProcessing();

      const createdClientId = getClientId(created);
      let createdDetail = null;
      if (createdClientId) {
        try {
          createdDetail = await getClientById(createdClientId);
        } catch {
          // Client creation succeeded; the list refresh below remains a fallback.
        }
      }

      const updated = await refreshClientsForUser();

      const listedClient =
        updated.find(
          (c) =>
            (createdClientId &&
              String(getClientId(c)) === String(createdClientId)) ||
            String(c.clientName ?? c.Client_Name ?? "").trim() === trimmedName,
        ) || null;

      const newClient = createdDetail
        ? {
            ...listedClient,
            ...createdDetail,
            projects: listedClient?.projects ?? createdDetail.projects ?? [],
          }
        : listedClient || (createdClientId ? created : null);

      if (newClient) {
        setClients((current) => {
          const index = current.findIndex(
            (client) =>
              String(getClientId(client)) === String(getClientId(newClient)),
          );
          if (index < 0) return [...current, newClient];

          const next = [...current];
          next[index] = newClient;
          return next;
        });
        await selectClient(newClient);
      }
    } catch (err) {
      showAlert(
        "Error",
        getUserErrorMessage(err, "Failed to create client."),
      );
      throw err;
    } finally {
      setLoading(false);
      endSaveProgress();
    }
  };

  const updateClient = async (clientData) => {
    const clientId = getClientId(clientData);
    const trimmedName = String(clientData?.clientName ?? "").trim();
    if (!clientId || !trimmedName) return;

    const fileCount = countClientUploadFiles(clientData);
    setLoading(true);
    beginSaveProgress(fileCount);

    try {
      await updateClientRequest(
        {
        ...clientData,
        clientId,
        clientName: trimmedName,
        domainName: String(clientData?.domainName ?? "").trim(),
        },
        {
          onUploadProgress: createUploadProgressHandler({
            fileCount,
            onProgress: setSaveProgress,
          }),
        },
      );

      markSaveProcessing();

      let updatedDetail = null;
      try {
        updatedDetail = await getClientById(clientId);
      } catch {
        // The update succeeded; the list refresh below remains a fallback.
      }

      const updated = await refreshClientsForUser();
      const listedClient =
        updated.find((c) => String(getClientId(c)) === String(clientId)) ||
        null;

      const refreshed = updatedDetail
        ? {
            ...listedClient,
            ...updatedDetail,
            projects: listedClient?.projects ?? updatedDetail.projects ?? [],
          }
        : listedClient;

      if (refreshed) {
        setClients((current) => {
          const index = current.findIndex(
            (client) => String(getClientId(client)) === String(clientId),
          );
          if (index < 0) return current;

          const next = [...current];
          next[index] = refreshed;
          return next;
        });

        if (String(getClientId(selectedClient)) === String(clientId)) {
          setSelectedClientState(refreshed);
          applyClientProjects(refreshed);
          // The client id is unchanged, so the background only reloads its
          // slides when it is told the theme list moved on.
          window.dispatchEvent(new Event("client-theme-updated"));
        }
      }
    } catch (err) {
      showAlert(
        "Error",
        getUserErrorMessage(err, "Failed to update client."),
      );
      throw err;
    } finally {
      setLoading(false);
      endSaveProgress();
    }
  };

  const resolveProjectTypeName = async (rawTypeName) => {
    let projectTypeName = String(rawTypeName ?? "").trim();
    if (!projectTypeName) return projectTypeName;

    const types = await getAllProjectTypes();
    const match = types.find(
      (type) =>
        String(type.projectType ?? "").toLowerCase() ===
        projectTypeName.toLowerCase(),
    );
    if (!match) {
      const created = await createProjectType(projectTypeName);
      projectTypeName = String(created.projectType ?? projectTypeName).trim();
    }
    return projectTypeName;
  };

  const resolveProjectStatus = (projectData) =>
    projectData?.projectStatus &&
    typeof projectData.projectStatus === "object" &&
    !Array.isArray(projectData.projectStatus)
      ? projectData.projectStatus
      : keyDatesToProjectStatus(projectData?.keyDates);

  const refreshSelectedClientProjects = async () => {
    const updatedClients = await refreshClientsForUser();
    const refreshedClient = updatedClients.find(
      (client) =>
        String(getClientId(client)) === String(getClientId(selectedClient)),
    );

    if (refreshedClient) {
      setSelectedClientState(refreshedClient);
      applyClientProjects(refreshedClient);
    }
  };

  const createProject = async (projectData) => {
    const trimmedName = String(projectData?.projectName ?? "").trim();
    if (!trimmedName) return;

    setLoading(true);

    try {
      const projectTypeName = await resolveProjectTypeName(
        projectData?.projectTypeName,
      );

      await createProjectRequest({
        clientsId: getClientId(selectedClient),
        projectName: trimmedName,
        modelId: Number(projectData?.modelId ?? 1),
        projectTypeName,
        knowledgeSharingProjectIds: projectData?.knowledgeSharingProjectIds,
        knowledgeBank: projectData?.knowledgeBank ?? [],
        users: projectData?.users,
        projectStatus: resolveProjectStatus(projectData),
      });

      await refreshSelectedClientProjects();
    } catch (err) {
      showAlert(
        "Error",
        getUserErrorMessage(err, "Failed to create project."),
      );
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateProject = async (projectData) => {
    const projectId = getProjectId(projectData);
    const trimmedName = String(projectData?.projectName ?? "").trim();
    if (!projectId || !trimmedName) return;

    setLoading(true);

    try {
      const projectTypeName = await resolveProjectTypeName(
        projectData?.projectTypeName,
      );
      const clientsId =
        projectData?.clientsId ??
        projectData?.clientId ??
        getClientId(selectedClient);

      await updateProjectRequest({
        projectId,
        clientsId,
        projectName: trimmedName,
        modelId: Number(projectData?.modelId ?? 1),
        projectTypeName,
        knowledgeBank: projectData?.knowledgeBank ?? [],
        users: projectData?.users,
        projectStatus: resolveProjectStatus(projectData),
      });

      await refreshSelectedClientProjects();

      setSelectedProject((current) => {
        if (String(getProjectId(current) ?? "") !== String(projectId)) {
          return current;
        }
        return {
          ...current,
          projectName: trimmedName,
          projectTypeName: projectTypeName || current?.projectTypeName,
        };
      });
    } catch (err) {
      showAlert(
        "Error",
        getUserErrorMessage(err, "Failed to update project."),
      );
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteProject = async (projectId) => {
    if (!projectId) return;

    setLoading(true);
    try {
      await deleteProjectRequest(projectId);
      const updatedClients = await refreshClientsForUser();
      const refreshedClient = updatedClients.find(
        (client) =>
          String(getClientId(client)) === String(getClientId(selectedClient)),
      );

      if (refreshedClient) {
        setSelectedClientState(refreshedClient);
        applyClientProjects(refreshedClient);
      }

      const selectedProjectId =
        selectedProject?.projectId ??
        selectedProject?.Id ??
        selectedProject?.id ??
        null;
      if (String(selectedProjectId ?? "") === String(projectId)) {
        setSelectedProject(null);
      }
    } catch (err) {
      showAlert(
        "Error",
        getUserErrorMessage(err, "Failed to delete project."),
      );
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateProjectIsArchived = async (projectId, isArchived) => {
    if (!projectId) return;

    setLoading(true);
    try {
      await updateProjectArchivedRequest(projectId, isArchived);
      const updatedClients = await refreshClientsForUser();
      const refreshedClient = updatedClients.find(
        (client) =>
          String(getClientId(client)) === String(getClientId(selectedClient)),
      );

      if (refreshedClient) {
        setSelectedClientState(refreshedClient);
        applyClientProjects(refreshedClient);
      }

      setSelectedProject((current) =>
        String(getProjectId(current) ?? "") === String(projectId) ? null : current,
      );
    } catch (err) {
      showAlert(
        "Error",
        getUserErrorMessage(err, "Failed to update project archive status."),
      );
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const selectProject = (project) => {
    setSelectedProject(project ?? null);
    const projectId = getProjectId(project);
    if (projectId == null) return;

    setProjects((prev) => {
      const index = prev.findIndex(
        (p) => String(getProjectId(p)) === String(projectId),
      );
      if (index <= 0) return prev;
      const next = [...prev];
      const [item] = next.splice(index, 1);
      return [item, ...next];
    });
  };

  const refreshProjectFiles = async (projectId) => {
    if (projectId == null || String(projectId).trim() === "") return null;

    try {
      const raw = await getFileByProject(projectId);
      const files = extractProjectFilesList(raw);
      const count = files.length;
      const patch = { files, sourcesCount: count, fileCount: count };

      let merged = null;
      setProjects((prev) =>
        prev.map((p) => {
          const id = p?.projectId ?? p?.Id ?? p?.id;
          if (String(id) !== String(projectId)) return p;
          merged = { ...p, ...patch };
          return merged;
        }),
      );

      setSelectedProject((sel) => {
        if (!sel) return sel;
        const sid = sel?.projectId ?? sel?.Id ?? sel?.id;
        if (String(sid) !== String(projectId)) return sel;
        return { ...sel, ...patch };
      });

      return merged;
    } catch (err) {
      showAlert(
        "Error",
        getUserErrorMessage(err, "Failed to refresh project files."),
      );
      throw err;
    }
  };

  // Clear client selection as soon as auth drops (logout). Doing this only in
  // an effect would leave selectedClient set for the first login-page paint,
  // so Background could briefly load the previous client's theme.
  useEffect(() => {
    if (!userInfo) {
      setClients([]);
      setSelectedClientState(null);
      setProjects([]);
      setArchivedProjects([]);
      setSelectedProject(null);
      localStorage.removeItem(PREFIX + "selectedClient");
      return;
    }

    const loadClients = async () => {
      try {
        const data = await getClientsByUserName(userInfo?.username);
        setClients(data || []);
      } catch {
        showAlert("Error", "Failed to fetch clients");
      }
    };

    loadClients();
  }, [userInfo]);

  useEffect(() => {
    if (!userInfo) return;
    if (!clients.length) return;

    const stored = localStorage.getItem(PREFIX + "selectedClient");

    if (!stored) {
      redirectToHomepage();
      return;
    }

    try {
      const { clientId } = JSON.parse(stored);

      const found = clients.find(
        (c) => String(getClientId(c)) === String(clientId),
      );

      if (found) {
        selectClient(found, false);
      } else {
        localStorage.removeItem(PREFIX + "selectedClient");
        redirectToHomepage();
      }
    } catch {
      localStorage.removeItem(PREFIX + "selectedClient");
      redirectToHomepage();
    }
  }, [clients, userInfo]);

  return (
    <ClientContext.Provider
      value={{
        clients,
        selectedClient,
        setSelectedClient: selectClient,
        projects,
        archivedProjects,
        selectedProject,
        setSelectedProject,
        selectProject,
        loading,
        saveProgress,
        createClient,
        updateClient,
        createProject,
        updateProject,
        deleteProject,
        updateProjectIsArchived,
        refreshProjectFiles,
      }}
    >
      {children}
    </ClientContext.Provider>
  );
}
